import { useTranslation } from '@pancakeswap/localization'
import { Box, Button, ErrorState, Flex, Skeleton, Text } from '@pancakeswap/uikit'
import { useState } from 'react'
import styled from 'styled-components'
import type { DataMeta } from 'server/info/types'
import { timeAgo } from './format'

const Bar = styled(Flex)<{ $tone: 'ok' | 'warning' | 'danger' | 'muted' }>`
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme, $tone }) => ($tone === 'danger' ? theme.colors.failure : $tone === 'warning' ? theme.colors.warning : theme.colors.cardBorder)};
  background: ${({ theme }) => theme.colors.backgroundAlt};
  font-size: 13px;
`
const Line = styled(Flex)`
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  font-size: 13px;
  min-height: 28px;
`
const Dot = styled.span<{ $tone: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${({ theme, $tone }) => ($tone === 'danger' ? theme.colors.failure : $tone === 'warning' ? theme.colors.warning : $tone === 'ok' ? theme.colors.success : theme.colors.textSubtle)};
`
const Details = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.textSubtle};
  max-height: 240px;
  overflow: auto;
`

export const stateLabel = (state: DataMeta['state'], stale: boolean) =>
  state === 'unavailable' ? 'Unavailable' : state === 'partial' ? 'Partial data' : state === 'empty' ? 'No data (confirmed empty)' : stale ? 'Stale' : 'Live'

/**
 * One line per data section (brief §5): state, last update, last indexed block, coverage; technical
 * detail behind a toggle; manual retry. Never a silent zero — the section that fails says so.
 */
export const DataStateBar: React.FC<{ meta?: DataMeta; loading?: boolean; error?: Error; onRetry?: () => void; compact?: boolean; inline?: boolean }> = ({ meta, loading, error, onRetry, compact, inline }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  if (loading && !meta) {
    if (inline) return <Skeleton width="160px" height="16px" />
    return (
      <Bar $tone="muted" aria-busy="true">
        <Skeleton width="160px" height="16px" />
      </Bar>
    )
  }
  if (error && !meta) {
    return <ErrorState title={t('Data service unavailable')} description={error.message} onRetry={onRetry} retryLabel={t('Retry')} tone="danger" py="16px" />
  }
  if (!meta) return null
  const ageMs = Date.now() - new Date(meta.updatedAt).getTime()
  const stale = ageMs > 5 * 60_000
  const tone = meta.state === 'unavailable' ? 'danger' : meta.state === 'partial' || stale ? 'warning' : meta.state === 'empty' ? 'muted' : 'ok'
  const hasDetails = meta.warnings.length > 0 || !!meta.priceMethod || !!meta.coverage
  if (inline) {
    // one line next to the section title: state, age, block; the technical detail stays behind the toggle
    return (
      <Box style={{ maxWidth: '100%' }}>
        <Line role="status" data-testid="data-state-inline">
          <Text as="span" fontSize="13px" bold color={tone === 'danger' ? 'failure' : tone === 'warning' ? 'warning' : 'text'}>
            <Dot $tone={tone} />
            {stateLabel(meta.state, stale)}
          </Text>
          <Text as="span" fontSize="13px" color="textSubtle">
            {t('updated')} {timeAgo(meta.updatedAt)}
          </Text>
          {meta.lastIndexedBlock !== undefined && (
            <Text as="span" fontSize="13px" color="textSubtle">
              {t('block')} {meta.lastIndexedBlock.toLocaleString()}
            </Text>
          )}
          {hasDetails && (
            <Button scale="xs" variant="text" px="4px" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
              {open ? t('Hide details') : t('Details')}
            </Button>
          )}
          {onRetry && (meta.state === 'unavailable' || stale) && (
            <Button scale="xs" variant="secondary" onClick={onRetry}>
              {t('Retry')}
            </Button>
          )}
        </Line>
        {meta.state === 'unavailable' && meta.error && (
          <Text fontSize="13px" color="failure">
            {meta.error}
          </Text>
        )}
        {open && (
          <Details>
            {`source: ${meta.source}\n`}
            {meta.priceMethod ? `price method: ${meta.priceMethod}\n` : ''}
            {meta.coverage ? `coverage: ${meta.coverage}\n` : ''}
            {meta.warnings.map((w) => `note: ${w}`).join('\n')}
          </Details>
        )}
      </Box>
    )
  }
  return (
    <Box>
      <Bar $tone={tone} role="status">
        <Text as="span" fontSize="13px" bold>
          <Dot $tone={tone} />
          {stateLabel(meta.state, stale)}
        </Text>
        {meta.state === 'unavailable' && meta.error && (
          <Text as="span" fontSize="13px" color="failure" style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {meta.error}
          </Text>
        )}
        <Text as="span" fontSize="13px" color="textSubtle">
          {t('Updated')} {timeAgo(meta.updatedAt)}
        </Text>
        {meta.lastIndexedBlock !== undefined && (
          <Text as="span" fontSize="13px" color="textSubtle">
            {t('Block')} {meta.lastIndexedBlock.toLocaleString()}
            {meta.lastIndexedAt ? ` (${timeAgo(meta.lastIndexedAt)})` : ''}
          </Text>
        )}
        {!compact && meta.coverage && (
          <Text as="span" fontSize="13px" color="textSubtle" style={{ flexBasis: '100%' }}>
            {t('Coverage')}: {meta.coverage}
          </Text>
        )}
        <Flex ml="auto" style={{ gap: 8 }}>
          {hasDetails && (
            <Button scale="xs" variant="text" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
              {open ? t('Hide details') : t('Details')}
            </Button>
          )}
          {onRetry && (
            <Button scale="xs" variant="secondary" onClick={onRetry}>
              {t('Retry')}
            </Button>
          )}
        </Flex>
      </Bar>
      {open && (
        <Details>
          {`source: ${meta.source}\n`}
          {meta.priceMethod ? `price method: ${meta.priceMethod}\n` : ''}
          {meta.coverage ? `coverage: ${meta.coverage}\n` : ''}
          {meta.warnings.map((w) => `note: ${w}`).join('\n')}
        </Details>
      )}
    </Box>
  )
}

export default DataStateBar
